import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Supported languages
export type Language = 'en' | 'zh';

// Language context type
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isLoading: boolean;
}

// Create the context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Language Provider Props
interface LanguageProviderProps {
  children: ReactNode;
}

// Language Provider Component
export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>('en');
  const [isLoading, setIsLoading] = useState(true);

  // Load saved language preference on mount
  useEffect(() => {
    // Check if we're in the browser environment
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const savedLanguage = localStorage.getItem('auren-language') as Language;
      if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'zh')) {
        setLanguageState(savedLanguage);
      }
    }
    setIsLoading(false);
  }, []);

  // Function to change language
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    // Check if we're in the browser environment
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      localStorage.setItem('auren-language', lang);
    }
    // Update document lang attribute for accessibility
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    isLoading,
  };

  return React.createElement(
    LanguageContext.Provider,
    { value },
    children
  );
}

// Custom hook to use language context
export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Helper function to get language display name
export function getLanguageDisplayName(lang: Language): string {
  const names = {
    en: 'English',
    zh: '中文',
  };
  return names[lang];
}

// Re-export useTranslation hook for convenience
export { useTranslation } from './useTranslation';
export type { TranslationFunction } from './useTranslation';
